import { black, white, gray, blue, green, orange, purple, red, yellow } from 'primer-colors';
import { fontSizes, lineHeights } from 'primer-typography';
import * as styledSystem from 'styled-system';
import { space, width, style, compose, get, themeGet } from 'styled-system';
import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import PropTypes from 'prop-types';
import Octicon, { GitMerge, GitPullRequest, IssueClosed, IssueOpened } from '@githubprimer/octicons-react';
import nanoid from 'nanoid';
import classnames from 'classnames';

var colors = {
  bodytext: gray[9],
  black: black,
  white: white,
  gray: gray,
  blue: blue,
  green: green,
  orange: orange,
  purple: purple,
  red: red,
  yellow: yellow,
  blackfade15: 'rgba(27, 31, 35, 0.15)',
  blackfade20: 'rgba(27, 31, 35, 0.20)',
  blackfade35: 'rgba(27, 31, 35, 0.35)',
  blackfade50: 'rgba(27, 31, 35, 0.5)',
  whitefade15: 'rgba(255, 255, 255, 0.15)',
  state: {
    error: red[5],
    failure: red[5],
    pending: yellow[7],
    queued: yellow[7],
    success: green[5],
    unknown: gray[4]
  },
  button: {
    disabledColor: 'rgba(36, 41, 46, 0.4)',
    bg2: 'rgb(239, 243, 246)',
    border: 'rgba(27, 31, 35, 0.2)',
    focusShadow: 'rgba(3, 102, 214, 0.3)',
    activeBg: 'rgb(233, 236, 239)',
    hoverBg: 'rgb(230, 235, 241)',
    dangerActiveBg: 'rgb(181, 32, 44)',
    dangerFocusShadow: 'rgba(203, 36, 49, 0.4)',
    dangerHoverBgImage: 'rgb(222, 68, 80)',
    white: 'rgb(255, 255, 255)',
    outlineBlue: 'rgb(3, 102, 214)',
    outlineShadow: 'rgba(3, 102, 214, 0.4)',
    primaryBg: 'rgb(40, 167, 69)',
    primaryBgImage: 'rgb(52, 208, 88)',
    primaryHoverBg: 'rgb(38, 159, 66)',
    primaryHoverBgImage: 'rgb(47, 203, 83)',
    primaryBorder: 'rgba(27, 31, 35, 0.5)',
    primaryActiveBg: 'rgb(39, 159, 67)',
    primaryActiveShadow: 'rgba(27, 31, 35, 0.15)',
    primaryFocusShadow: 'rgba(52, 208, 88, 0.4)'
  },
  counter: {
    bg: 'rgba(27, 31, 35, 0.08)'
  },
  filterList: {
    hoverBg: '#eaecef'
  }
};
var theme = {
  breakpoints: ['544px', '768px', '1012px', '1280px'],
  maxWidths: {
    small: '544px',
    medium: '768px',
    large: '1012px',
    xlarge: '1280px'
  },
  fonts: {
    normal: fontStack(['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Helvetica', 'Arial', 'sans-serif', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol']),
    mono: fontStack(['SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', 'Courier', 'monospace'])
  },
  fontWeights: {
    light: 300,
    normal: 400,
    bold: 600
  },
  colors: colors,
  borders: [0, '1px solid'],
  fontSizes: fontSizes,
  lineHeights: lineHeights,
  radii: [0, 3, 6],
  shadows: {
    small: '0 1px 1px rgba(27, 31, 35, 0.1)',
    medium: '0 1px 5px rgba(27, 31, 35, 0.15)',
    large: '0 1px 15px rgba(27, 31, 35, 0.15)',
    'extra-large': '0 10px 50px rgba(27, 31, 35, 0.07)',
    formControl: 'rgba(27, 31, 35, 0.075) 0px 1px 2px inset',
    formControlFocus: 'rgba(3, 102, 214, 0.3) 0px 0px 0px 0.2em'
  },
  space: [0, 4, 8, 16, 24, 32, 40, 48, 64, 80, 96, 112, 128]
};

function fontStack(fonts) {
  return fonts.map(function (font) {
    return font.includes(' ') ? "\"".concat(font, "\"") : font;
  }).join(', ');
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = new Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  }
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (Symbol.iterator in Object(iter) || Object.prototype.toString.call(iter) === "[object Arguments]") return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var compose$1 = compose,
    getKey = get,
    themeGet$1 = themeGet;
var get$1 = function get$$1(key) {
  return themeGet$1(key, getKey(theme, key));
};
var COMMON_LIST = ['color', 'space'];
var BORDER_LIST = ['borders', 'borderColor', 'boxShadow', 'borderRadius'];
var TYPOGRAPHY_LIST = [// typography props
'fontFamily', 'fontSize', 'fontStyle', 'fontWeight', 'lineHeight', 'textAlign'];
var LAYOUT_LIST = [// layout props
'display', 'size', 'width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight', 'overflow', 'verticalAlign'];
var POSITION_LIST = [// position props
'position', 'zIndex', 'top', 'right', 'bottom', 'left'];
var FLEX_CONTAINER_LIST = [// flex container props (display: flex)
'flexBasis', 'flexDirection', 'flexWrap', 'alignContent', 'alignItems', 'justifyContent', 'justifyItems'];
var FLEX_ITEM_LIST = [// flex container child props
'flex', 'justifySelf', 'alignSelf', 'order'];
var COMMON = composeList(COMMON_LIST);
var BORDER = composeList(BORDER_LIST);
var TYPOGRAPHY = composeList(TYPOGRAPHY_LIST);
var LAYOUT = composeList(LAYOUT_LIST);
var POSITION = composeList(POSITION_LIST);
var FLEX_CONTAINER = composeList(FLEX_CONTAINER_LIST);
var FLEX_ITEM = composeList(FLEX_ITEM_LIST);

function composeList(list) {
  return compose$1.apply(void 0, _toConsumableArray(list.map(function (name) {
    return styledSystem[name];
  })));
}

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  * { box-sizing: border-box; }\n  body { margin: 0; }\n  table { border-collapse: collapse; }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var GlobalStyle = createGlobalStyle(_templateObject());

var Base = function Base(props) {
  var color = props.color,
      lineHeight = props.lineHeight,
      fontFamily = props.fontFamily,
      theme$$1 = props.theme,
      rest = _objectWithoutProperties(props, ["color", "lineHeight", "fontFamily", "theme"]);

  return React.createElement("div", rest, React.createElement(GlobalStyle, null), props.children);
};

Base.displayName = "Base";
var BaseStyles = styled(Base).withConfig({
  displayName: "BaseStyles",
  componentId: "sc-8q0tj8-0"
})(["", " ", ";"], TYPOGRAPHY, COMMON);
BaseStyles.defaultProps = {
  color: 'gray.9',
  fontFamily: 'normal',
  lineHeight: 'default',
  theme: theme
};
BaseStyles.propTypes = _objectSpread({}, TYPOGRAPHY.propTypes, COMMON.propTypes, {
  theme: PropTypes.object
});

var Box = styled.div.withConfig({
  displayName: "Box",
  componentId: "sc-1b6inku-0"
})(["", " ", ";"], LAYOUT, COMMON);
Box.defaultProps = {
  theme: theme
};
Box.propTypes = _objectSpread({}, LAYOUT.propTypes, COMMON.propTypes, {
  theme: PropTypes.object
});

var BorderBox = styled(Box)(BORDER);
BorderBox.defaultProps = {
  theme: theme,
  border: '1px solid',
  borderColor: 'gray.2',
  borderRadius: 1
};
BorderBox.propTypes = _objectSpread({
  theme: PropTypes.object
}, Box.propTypes, BORDER.propTypes);

var Flex = styled(Box).withConfig({
  displayName: "Flex",
  componentId: "jlpa5r-0"
})(["", ";"], FLEX_CONTAINER);
Flex.Item = styled(Box).withConfig({
  displayName: "Flex__Item",
  componentId: "jlpa5r-1"
})(["", ";"], FLEX_ITEM);
Flex.defaultProps = {
  theme: theme,
  display: 'flex'
};
Flex.Item.defaultProps = {
  theme: theme
};
Flex.propTypes = _objectSpread({}, Box.propTypes, FLEX_CONTAINER.propTypes);
Flex.Item.propTypes = _objectSpread({}, FLEX_ITEM.propTypes, {
  theme: PropTypes.object
});

var Position = styled.div.withConfig({
  displayName: "Position",
  componentId: "gj4o9g-0"
})(["", " ", " ", ""], LAYOUT, COMMON, POSITION);
Position.defaultProps = {
  theme: theme
};
Position.propTypes = _objectSpread({}, COMMON.propTypes, LAYOUT.propTypes, POSITION.propTypes, {
  theme: PropTypes.object
});

function withPosition(position) {
  var WithPosition = function WithPosition(props) {
    return React.createElement(Position, _extends({}, props, {
      position: position
    }));
  };

  WithPosition.defaultProps = Position.defaultProps;
  WithPosition.displayName = "Position.".concat(position);
  return WithPosition;
}

var Absolute = withPosition('absolute');
var Fixed = withPosition('fixed');
var Relative = withPosition('relative');
var Sticky = withPosition('sticky');
Sticky.defaultProps = {
  theme: theme,
  top: 0,
  zIndex: 1
};

function borderRadius(_ref) {
  var size = _ref.size;
  return {
    borderRadius: size <= 24 ? '2px' : '3px'
  };
}

var Avatar = styled.img.attrs(function (props) {
  return {
    height: props.size,
    width: props.size
  };
}).withConfig({
  displayName: "Avatar",
  componentId: "cqsihq-0"
})(["display:inline-block;overflow:hidden;line-height:", ";vertical-align:middle;", ";", ";"], get$1('lineHeights.condensedUltra'), borderRadius, space);
Avatar.defaultProps = {
  theme: theme,
  size: 20,
  alt: ''
};
Avatar.propTypes = _objectSpread({
  alt: PropTypes.string.isRequired,
  size: PropTypes.number,
  src: PropTypes.string
}, space.propTypes, {
  theme: PropTypes.object
});

var getBackgroundColor = get$1('colors.white');
var Wrapper = styled('div').withConfig({
  displayName: "AvatarPair__Wrapper",
  componentId: "sc-1v5x8y5-0"
})(["display:inline-flex;position:relative;"]);

var childStyles = function childStyles(props) {
  return {
    display: 'inline-block',
    overflow: 'hidden',
    // Ensure page layout in Firefox should images fail to load
    lineHeight: "".concat(get$1('lineHeights.condensedUltra')),
    verticalAlign: 'middle',
    borderRadius: '2px',
    position: 'absolute',
    right: '-15%',
    bottom: '-9%',
    boxShadow: '-2px -2px 0 rgba(255,255,255,0.8)',
    backgroundColor: "".concat(getBackgroundColor(props))
  };
};

var ChildAvatar = styled(Avatar)(childStyles);

var AvatarPair = function AvatarPair(_ref) {
  var children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["children"]);

  var avatars = React.Children.map(children, function (child, i) {
    return i === 0 ? React.cloneElement(child, {
      size: 40
    }) : React.createElement(ChildAvatar, _extends({}, child.props, {
      size: 20
    }));
  });
  return React.createElement(Wrapper, rest, avatars);
};

AvatarPair.displayName = "AvatarPair";
// styled() changes this
AvatarPair.displayName = 'AvatarPair';
AvatarPair.defaultProps = {
  theme: theme
};

var BranchName = styled.a.withConfig({
  displayName: "BranchName",
  componentId: "sc-1foytcp-0"
})(["display:inline-block;padding:2px 6px;font-size:", "px;font-family:", ";color:rgba(27,31,35,0.6);background-color:#eaf5ff;border-radius:3px;", ";"], get$1('fontSizes.0'), get$1('fonts.mono'), COMMON);
BranchName.defaultProps = {
  theme: theme
};
BranchName.propTypes = _objectSpread({
  href: PropTypes.string
}, COMMON.propTypes, {
  theme: PropTypes.object
});

var getButtonStyles = function getButtonStyles(theme) {
  return "\n      position: relative;\n      display: inline-block;\n      padding: 6px 12px;\n      color: ".concat(get$1('colors.gray.9')(theme), ";\n      background-color: ").concat(get$1('colors.gray.1')(theme), ";\n      background-image: linear-gradient(-180deg, ").concat(get$1('colors.gray.0')(theme), " 0%, ").concat(get$1('colors.button.bg2')(theme), " 90%);\n      font-size: ").concat(get$1('fontSizes.1')(theme), "px;\n      font-weight: ").concat(get$1('fontWeights.bold')(theme), ";\n      line-height: 20px;\n      white-space: nowrap;\n      vertical-align: middle;\n      cursor: pointer;\n      user-select: none;\n      background-repeat: repeat-x;\n      background-position: -1px -1px;\n      background-size: 110% 110%;\n      border: 1px solid ").concat(get$1('colors.button.border')(theme), ";\n      border-radius: 0.25em;\n      appearance: none;\n\n      &:hover {\n        background-color: ").concat(get$1('colors.button.hoverBg')(theme), ";\n        background-image: linear-gradient(-180deg, ").concat(get$1('colors.button.bg2')(theme), " 0%, ").concat(get$1('colors.button.hoverBg')(theme), " 90%);\n        background-position: -0.5em center;\n        border-color: ").concat(get$1('colors.blackfade35')(theme), ";\n        text-decoration: none;\n        background-repeat: repeat-x;\n      }\n\n      &:active {\n        background-color: ").concat(get$1('colors.button.activeBg')(theme), ";\n        background-image: none;\n        box-shadow: ").concat(get$1('colors.blackfade15')(theme), " 0px 0.15em 0.3em inset;\n        border-color: ").concat(get$1('colors.button.border')(theme), "; //convert black to rbg here\n      }\n\n      &:selected {\n        background-color: ").concat(get$1('colors.button.activeBg')(theme), ";\n        background-image: none;\n        box-shadow: ").concat(get$1('colors.blackfade15')(theme), " 0px 0.15em 0.3em inset;\n        border-color: ").concat(get$1('colors.button.border')(theme), ";\n      }\n\n      &.disabled {\n        color: ").concat(get$1('colors.button.disabledColor')(theme), "!important;\n        background-color: ").concat(get$1('colors.gray.1')(theme), "!important;\n        background-image: none!important;\n        border-color: ").concat(get$1('colors.blackfade20')(theme), "!important;\n        box-shadow: none!important;\n      }\n\n      &:focus {\n        outline: none;\n        box-shadow: ").concat(get$1('colors.button.focusShadow')(theme), " 0px 0px 0px 0.2em;\n      }\n    ");
};

function fontSize(_ref) {
  var _ref$size = _ref.size,
      size = _ref$size === void 0 ? '14px' : _ref$size,
      props = _objectWithoutProperties(_ref, ["size"]);

  return {
    fontSize: size === 'sm' ? "".concat(get$1('fontSizes.0')(props), "px") : size === 'large' ? "".concat(get$1('fontSizes.2')(props), "px") : size
  };
}

var Button = styled.button.attrs(function (props) {
  return {
    onClick: props.disabled ? undefined : props.onClick,
    className: props.disabled ? 'disabled' : ''
  };
}).withConfig({
  displayName: "Button",
  componentId: "sc-14jla3s-0"
})(["", ";", ";", ";", ";"], function (props) {
  return props.theme ? getButtonStyles(props.theme) : '';
}, fontSize, COMMON, width);
Button.defaultProps = {
  theme: theme
};
Button.propTypes = _objectSpread({
  as: PropTypes.oneOfType([PropTypes.oneOf(['button', 'a', 'summary', 'input']), PropTypes.func]),
  children: PropTypes.node,
  disabled: PropTypes.bool,
  grouped: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'large']),
  theme: PropTypes.object
}, COMMON.propTypes, width.propTypes);

var ButtonDanger = styled(Button).withConfig({
  displayName: "ButtonDanger",
  componentId: "sc-1dt7qaa-0"
})(["color:", ";background-color:", ";background-image:linear-gradient(-180deg,", " 0%,", " 90%);&:hover{color:", ";background-color:", ";background-image:linear-gradient( -180deg,", " 0%,", " 90% );border-color:", ";}&:focus{box-shadow:", " 0px 0px 0px 0.2em;}&:active{color:", ";background-color:", ";background-image:none;box-shadow:", " 0px 0.15em 0.3em inset;border-color:", ";}&:selected{color:", ";background-color:", ";background-image:none;box-shadow:", " 0px 0.15em 0.3em inset;border-color:", ";}"], get$1('colors.red.6'), get$1('colors.gray.0'), get$1('colors.gray.0'), get$1('colors.button.bg2'), get$1('colors.white'), get$1('colors.red.6'), get$1('colors.button.dangerHoverBgImage'), get$1('colors.red.6'), get$1('colors.blackfade50'), get$1('colors.button.dangerFocusShadow'), get$1('colors.white'), get$1('colors.button.dangerActiveBg'), get$1('colors.blackfade15'), get$1('colors.blackfade50'), get$1('colors.white'), get$1('colors.button.dangerActiveBg'), get$1('colors.blackfade15'), get$1('colors.blackfade50'));

var ButtonOutline = styled(Button).withConfig({
  displayName: "ButtonOutline",
  componentId: "sc-171r0y7-0"
})(["color:", ";background-color:", ";background-image:none;&:hover{color:", ";background-color:", ";background-image:none;border-color:", ";}&:active{color:", ";background-color:", ";background-image:none;border-color:", ";}&:focus{box-shadow:", " 0px 0px 0px 0.2em;border-color:", ";}"], get$1('colors.button.outlineBlue'), get$1('colors.button.white'), get$1('colors.button.white'), get$1('colors.button.outlineBlue'), get$1('colors.button.outlineBlue'), get$1('colors.button.white'), get$1('colors.button.outlineBlue'), get$1('colors.button.outlineBlue'), get$1('colors.button.outlineShadow'), get$1('colors.button.outlineBlue'));

var ButtonPrimary = styled(Button).withConfig({
  displayName: "ButtonPrimary",
  componentId: "efigdw-0"
})(["color:", ";background-color:", ";background-image:linear-gradient( -180deg,", " 0%,", " 90% );&:hover{background-color:", ";background-image:linear-gradient( -180deg,", " 0%,", " 90% );background-position:-0.5em center;border-color:", ";}&:active{background-color:", ";background-image:none;box-shadow:", " 0px 0.15em 0.3em inset;border-color:", ";}&:focus{box-shadow:rgba(52,208,88,0.4) 0px 0px 0px 0.2em;}"], get$1('colors.button.white'), get$1('colors.button.primaryBg'), get$1('colors.button.primaryBgImage'), get$1('colors.button.primaryBg'), get$1('colors.button.primaryHoverBg'), get$1('colors.button.primaryHoverBgImage'), get$1('colors.button.primaryHoverBg'), get$1('colors.button.primaryBorder'), get$1('colors.button.primaryActiveBg'), get$1('colors.button.primaryActiveShadow'), get$1('colors.button.primaryBorder'));

var oppositeEdge = {
  top: 'Bottom',
  right: 'Left',
  bottom: 'Top',
  left: 'Right'
};
var perpendicularEdge = {
  top: 'Left',
  right: 'Top',
  bottom: 'Left',
  left: 'Top'
};

function getEdgeAlign(location) {
  var _location$split = location.split('-'),
      _location$split2 = _slicedToArray(_location$split, 2),
      edge = _location$split2[0],
      align = _location$split2[1];

  return [edge, align];
}

function getPosition(edge, align, spacing) {
  var _ref;

  var opposite = oppositeEdge[edge].toLowerCase();
  var perp = perpendicularEdge[edge].toLowerCase();
  return _ref = {}, _defineProperty(_ref, opposite, '100%'), _defineProperty(_ref, align || perp, align ? spacing : '50%'), _ref;
}

var getBg = style({
  prop: 'bg',
  key: 'colors'
});
var getBorderColor = style({
  prop: 'borderColor',
  key: 'colors'
});
var getBorderWidth = style({
  prop: 'borderWidth',
  key: 'borderWidths',
  scale: [0, 1]
});
var getSize = style({
  prop: 'size',
  key: 'space'
});

function Caret(props) {
  var _objectSpread2;

  var _getBg = getBg(props),
      bg = _getBg.bg;

  var _getBorderColor = getBorderColor(props),
      borderColor = _getBorderColor.borderColor;

  var _getBorderWidth = getBorderWidth(props),
      borderWidth = _getBorderWidth.borderWidth;

  var _getSize = getSize(props),
      size = _getSize.size;

  var location = props.location;

  var _getEdgeAlign = getEdgeAlign(location),
      _getEdgeAlign2 = _slicedToArray(_getEdgeAlign, 2),
      edge = _getEdgeAlign2[0],
      align = _getEdgeAlign2[1];

  var perp = perpendicularEdge[edge];

  var style$$1 = _objectSpread({
    pointerEvents: 'none',
    position: 'absolute'
  }, getPosition(edge, align, size), (_objectSpread2 = {}, _defineProperty(_objectSpread2, "margin".concat(perp), align ? null : -size), _defineProperty(_objectSpread2, "marginTop", '-1px'), _objectSpread2)); // note: these arrays represent points in the form [x, y]


  var a = [-size, 0];
  var b = [0, size];
  var c = [size, 0]; // spaces are optional in path `d` attribute, and points are
  // represented in the form `x,y` -- which is what the arrays above
  // become when stringified!

  var triangle = "M".concat(a, "L").concat(b, "L").concat(c, "L").concat(a, "Z");
  var line = "M".concat(a, "L").concat(b, "L").concat(c);
  var transform = {
    top: "translate(".concat([size, size * 2], ") rotate(180)"),
    right: "translate(".concat([0, size], ") rotate(-90)"),
    bottom: "translate(".concat([size, 0], ")"),
    left: "translate(".concat([size * 2, size], ") rotate(90)")
  }[edge];
  return React.createElement("svg", {
    width: size * 2,
    height: size * 2,
    style: style$$1
  }, React.createElement("g", {
    transform: transform
  }, React.createElement("path", {
    d: triangle,
    fill: bg
  }), React.createElement("path", {
    d: line,
    fill: "none",
    stroke: borderColor,
    strokeWidth: borderWidth
  })));
}

Caret.displayName = "Caret";
Caret.locations = ['top', 'top-left', 'top-right', 'right', 'right-top', 'right-bottom', 'bottom', 'bottom-left', 'bottom-right', 'left', 'left-top', 'left-bottom'];
Caret.defaultProps = {
  bg: 'white',
  borderColor: 'gray.2',
  borderWidth: 1,
  location: 'bottom',
  size: 2,
  theme: theme
};

var sizeMapper = function sizeMapper() {
  var size = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'medium';
  if (typeof size === 'number') return size;
  var map = {
    small: 56,
    medium: 96,
    large: 128
  };
  return map[size];
};

var sizeStyles = function sizeStyles(_ref) {
  var size = _ref.size;
  return {
    width: sizeMapper(size),
    height: sizeMapper(size)
  };
};

var CircleBadge = styled.div.withConfig({
  displayName: "CircleBadge",
  componentId: "sc-19eysyj-0"
})(["display:flex;align-items:center;justify-content:center;background-color:", ";border-radius:50%;box-shadow:", ";", " ", ";"], get$1('colors.white'), get$1('shadows.medium'), COMMON, sizeStyles);
var Icon = styled(Octicon).withConfig({
  displayName: "CircleBadge__Icon",
  componentId: "sc-19eysyj-1"
})(["max-width:60% !important;height:auto !important;max-height:55% !important;", ";"], COMMON);
CircleBadge.Icon = Icon;
CircleBadge.defaultProps = {
  theme: theme,
  size: 'medium'
};
CircleBadge.propTypes = _objectSpread({
  size: PropTypes.oneOfType([PropTypes.oneOf(['small', 'medium', 'large']), PropTypes.number]),
  theme: PropTypes.object
}, COMMON.propTypes);
CircleBadge.Icon.defaultProps = {
  theme: theme
};
CircleBadge.Icon.propsTypes = _objectSpread({
  theme: PropTypes.object
}, COMMON.propTypes);

function CircleOcticon(props) {
  var size = props.size,
      as = props.as;

  var icon = props.icon,
      bg = props.bg,
      asProp = props.as,
      rest = _objectWithoutProperties(props, ["icon", "bg", "as"]);

  return React.createElement(BorderBox, {
    as: as,
    bg: bg,
    overflow: "hidden",
    border: "none",
    size: size,
    borderRadius: "50%"
  }, React.createElement(Flex, _extends({}, rest, {
    alignItems: "center",
    justifyContent: "center"
  }), React.createElement(Octicon, {
    icon: icon,
    size: size
  })));
}

CircleOcticon.displayName = "CircleOcticon";
CircleOcticon.defaultProps = _objectSpread({
  theme: theme
}, Flex.defaultProps, {
  size: 32
});

var colorStyles = function colorStyles(_ref) {
  var scheme = _ref.scheme,
      props = _objectWithoutProperties(_ref, ["scheme"]);

  return {
    color: scheme === 'gray-light' ? get$1('colors.gray.9')(props) : scheme === 'gray' ? get$1('colors.white')(props) : get$1('colors.gray.6')(props)
  };
};

var bgStyles = function bgStyles(_ref2) {
  var scheme = _ref2.scheme,
      props = _objectWithoutProperties(_ref2, ["scheme"]);

  return {
    backgroundColor: scheme === 'gray-light' ? get$1('colors.blackfade15')(props) : scheme === 'gray' ? get$1('colors.gray.5')(props) : get$1('colors.counter.bg')(props)
  };
};

var CounterLabel = styled.span.withConfig({
  displayName: "CounterLabel",
  componentId: "dvbkn1-0"
})(["display:inline-block;padding:2px 5px;font-size:", "px;font-weight:", ";line-height:", ";border-radius:20px;", ";", ";", ";&:empty{visibility:hidden;}"], get$1('fontSizes.0'), get$1('fontWeights.bold'), get$1('lineHeights.condensedUltra'), colorStyles, bgStyles, COMMON);
CounterLabel.defaultProps = {
  theme: theme
};
CounterLabel.propTypes = _objectSpread({
  children: PropTypes.node,
  scheme: PropTypes.oneOf(['gray', 'gray-light']),
  theme: PropTypes.object
}, COMMON.propTypes);

var DetailsReset = styled('details').withConfig({
  displayName: "Details__DetailsReset",
  componentId: "sc-1uozfty-0"
})(["& > summary{list-style:none;}& > summary::-webkit-details-marker{display:none;}"]);

function getRenderer(children) {
  return typeof children === 'function' ? children : function () {
    return children;
  };
}

function DetailsBase(_ref) {
  var children = _ref.children,
      overlay = _ref.overlay,
      _ref$render = _ref.render,
      render = _ref$render === void 0 ? getRenderer(children) : _ref$render,
      rest = _objectWithoutProperties(_ref, ["children", "overlay", "render"]);

  var _useState = useState(Boolean(rest.open)),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  function toggle(event) {
    if (event) event.preventDefault();

    if (overlay) {
      openMenu();
    } else {
      setOpen(!open);
    }
  }

  function openMenu() {
    if (!open) {
      setOpen(true);
      document.addEventListener('click', closeMenu);
    }
  }

  function closeMenu(event) {
    if (event) event.preventDefault();
    setOpen(false);
    document.removeEventListener('click', closeMenu);
  }

  return React.createElement(DetailsReset, _extends({}, rest, {
    open: open,
    overlay: overlay
  }), render({
    open: open,
    toggle: toggle
  }));
}

DetailsBase.displayName = "DetailsBase";
var Details = styled(DetailsBase)(COMMON);
Details.defaultProps = {
  theme: theme,
  overlay: false
};
Details.propTypes = _objectSpread({
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
  open: PropTypes.bool,
  overlay: PropTypes.bool,
  render: PropTypes.func,
  theme: PropTypes.object
}, COMMON.propTypes);

var getDirectionStyles = function getDirectionStyles(theme, direction) {
  var map = {
    w: "\n      top: 0;\n      right: 100%;\n      left: auto;\n      width: auto;\n      margin-top: 0;\n      margin-right: 10px;\n\n      &::before {\n        top: 10px;\n        right: -16px;\n        left: auto;\n        border-color: transparent;\n        border-left-color: ".concat(get$1('colors.blackfade15')(theme), ";\n      }\n\n      &::after {\n        top: 11px;\n        right: -14px;\n        left: auto;\n        border-color: transparent;\n        border-left-color: ").concat(get$1('colors.white')(theme), ";\n      }\n    "),
    e: "\n      top: 0;\n      left: 100%;\n      width: auto;\n      margin-top: 0;\n      margin-left: 10px;\n\n      &::before {\n        top: 10px;\n        left: -16px;\n        border-color: transparent;\n        border-right-color: ".concat(get$1('colors.blackfade15')(theme), ";\n      }\n\n      &::after {\n        top: 11px;\n        left: -14px;\n        border-color: transparent;\n        border-right-color: ").concat(get$1('colors.white')(theme), ";\n      }\n    "),
    ne: "\n      top: auto;\n      bottom: 100%;\n      left: 0;\n      margin-bottom: 3px;\n\n      &::before,\n      &::after {\n        top: auto;\n        right: auto;\n      }\n\n      &::before {\n        bottom: -8px;\n        left: 9px;\n        border-top: 8px solid ".concat(get$1('colors.blackfade15')(theme), ";\n        border-bottom: 0;\n        border-left: 8px solid transparent;\n      }\n\n      &::after {\n        bottom: -7px;\n        left: 10px;\n        border-top: 7px solid ").concat(get$1('colors.white')(theme), ";\n        border-right: 7px solid transparent;\n        border-bottom: 0;\n        border-left: 7px solid transparent;\n      }\n    "),
    s: "\n      right: 50%;\n      left: auto;\n      transform: translateX(50%);\n\n      &::before {\n        top: -16px;\n        right: 50%;\n        transform: translateX(50%);\n      }\n\n      &::after {\n        top: -14px;\n        right: 50%;\n        transform: translateX(50%);\n      }\n    ",
    sw: "\n      right: 0;\n      left: auto;\n\n      &::before {\n        top: -16px;\n        right: 9px;\n        left: auto;\n      }\n\n      &::after {\n        top: -14px;\n        right: 10px;\n        left: auto;\n      }\n    ",
    se: "\n      &::before {\n        top: -16px;\n        left: 9px;\n      }\n\n      &::after {\n        top: -14px;\n        left: 10px;\n      }\n    "
  };
  return map[direction];
};

var DropdownBase = function DropdownBase(_ref) {
  var title = _ref.title,
      children = _ref.children,
      rest = _objectWithoutProperties(_ref, ["title", "children"]);

  return React.createElement(Details, _extends({
    overlay: true
  }, rest), function (_ref2) {
    var toggle = _ref2.toggle;
    return React.createElement(React.Fragment, null, React.createElement(Button, _extends({
      as: "summary",
      "aria-haspopup": "true",
      onClick: toggle
    }, rest), title, React.createElement(DropdownCaret, null)), children);
  });
};

DropdownBase.displayName = "DropdownBase";
var Dropdown = styled(DropdownBase).withConfig({
  displayName: "Dropdown",
  componentId: "sc-3wysgd-0"
})(["position:relative;display:inline-block;", ";"], COMMON);
var DropdownCaret = styled.div.withConfig({
  displayName: "Dropdown__DropdownCaret",
  componentId: "sc-3wysgd-1"
})(["border:", "px solid transparent;border-top-color:currentcolor;content:'';display:inline-block;height:0;vertical-align:middle;width:0;"], get$1('space.1'));
var DropdownMenu = styled.ul.withConfig({
  displayName: "Dropdown__DropdownMenu",
  componentId: "sc-3wysgd-2"
})(["background-clip:padding-box;background-color:", ";border:1px solid rgba(27,31,35,0.15);border-radius:", "px;box-shadow:0 3px 12px rgba(27,31,35,0.15);left:0;list-style:none;margin-top:2px;padding:5px 0 5px 0 !important;position:absolute;top:100%;width:160px;z-index:100;&::before{position:absolute;display:inline-block;content:'';}&::after{position:absolute;display:inline-block;content:'';}&::before{border:", "px solid transparent;border-bottom-color:", ";}&::after{border:7px solid transparent;border-bottom-color:", ";}> ul{list-style:none;}", ";", ";"], get$1('colors.white'), get$1('space.1'), get$1('space.2'), get$1('colors.blackfade15'), get$1('colors.white'), function (props) {
  return props.direction ? getDirectionStyles(props.theme, props.direction) : '';
}, COMMON);
var DropdownItem = styled.li.withConfig({
  displayName: "Dropdown__DropdownItem",
  componentId: "sc-3wysgd-3"
})(["display:block;padding:", "px 10px ", "px 15px;overflow:hidden;color:$get('colors.gray.9');text-overflow:ellipsis;white-space:nowrap;&:focus{color:", ";text-decoration:none;background-color:", ";}&:hover{color:", ";text-decoration:none;background-color:", ";outline:none;}", ";"], get$1('space.1'), get$1('space.1'), get$1('colors.white'), get$1('colors.blue.5'), get$1('colors.white'), get$1('colors.blue.5'), COMMON);
Dropdown.Menu = DropdownMenu;
Dropdown.Item = DropdownItem;
Dropdown.Menu.propTypes = _objectSpread({
  direction: PropTypes.oneOf(['ne', 'e', 'se', 's', 'sw', 'w'])
}, COMMON.propTypes);
Dropdown.Menu.defaultProps = {
  direction: 'sw',
  theme: theme
};
Dropdown.Item.defaultProps = {
  theme: theme
};
Dropdown.Item.propTypes = _objectSpread({}, COMMON.propTypes);
Dropdown.defaultProps = {
  theme: theme
};
Dropdown.propTypes = _objectSpread({
  children: PropTypes.node
}, COMMON.propTypes);

function ItemBase(_ref) {
  var children = _ref.children,
      count = _ref.count,
      theme$$1 = _ref.theme,
      rest = _objectWithoutProperties(_ref, ["children", "count", "theme"]);

  return React.createElement("a", _extends({
    key: nanoid()
  }, rest), count && React.createElement("span", {
    title: "results",
    className: "count"
  }, count), children);
}

ItemBase.displayName = "ItemBase";
var Item = styled(ItemBase).withConfig({
  displayName: "FilterList__Item",
  componentId: "sc-1widapo-0"
})(["position:relative;display:block;padding:", ";margin:", ";overflow:hidden;font-size:", "px;color:", ";background-color:", "!important;text-decoration:none;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;border-radius:", "px;&:hover{text-decoration:none;background-color:", ";}&:active{color:", ";background-color:", ";}.count{float:right;font-weight:", ";}", ":;"], function (props) {
  return props.small ? "".concat(get$1('space.1')(props), "px 10px") : "".concat(get$1('space.2')(props), "px 11px");
}, function (props) {
  return props.small ? '0 0 2px' : '0 0 5px 0';
}, get$1('fontSizes.1'), function (props) {
  return props.selected ? get$1('colors.white') : get$1('colors.gray.6');
}, function (props) {
  return props.selected ? get$1('colors.blue.5') : '';
}, get$1('radii.1'), get$1('colors.filterList.hoverBg'), get$1('colors.white'), get$1('colors.blue.5'), get$1('fontWeights.bold'), COMMON);

var FilterListBase = function FilterListBase(_ref2) {
  var children = _ref2.children,
      theme$$1 = _ref2.theme,
      rest = _objectWithoutProperties(_ref2, ["children", "theme"]);

  var items = React.Children.map(children, function (child) {
    return React.createElement("li", null, child);
  });
  return React.createElement("ul", rest, items);
};

FilterListBase.displayName = "FilterListBase";
var FilterList = styled(FilterListBase).withConfig({
  displayName: "FilterList",
  componentId: "sc-1widapo-1"
})(["list-style-type:none;", ";"], COMMON);
FilterList.defaultProps = {
  theme: theme,
  m: 0,
  p: 0
};
FilterList.propTypes = _objectSpread({
  children: PropTypes.node,
  small: PropTypes.bool
}, COMMON.propTypes);
FilterList.Item = Item;
FilterList.Item.defaultProps = {
  theme: theme
};
FilterList.Item.propTypes = _objectSpread({
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  children: PropTypes.node,
  className: PropTypes.string,
  count: PropTypes.string,
  selected: PropTypes.bool,
  theme: PropTypes.object
}, COMMON.propTypes);

var schemeMap = {
  green: {
    color: 'colors.green.8',
    bg: 'colors.green.1'
  },
  red: {
    color: 'colors.red.9',
    bg: 'colors.red.1'
  },
  yellow: {
    color: 'colors.yellow.9',
    bg: 'colors.yellow.1'
  },
  base: {
    color: 'colors.blue.8',
    bg: 'colors.blue.1'
  }
};
var Flash = styled.div.withConfig({
  displayName: "Flash",
  componentId: "y43qx-0"
})(["position:relative;padding:", "px;color:", ";background-color:", ";border-color:", ";border-style:solid;border-width:", ";border-radius:", ";margin-top:", ";p:last-child{margin-bottom:0;}", ";"], get$1('space.3'), function (props) {
  return get$1(schemeMap[props.scheme] ? schemeMap[props.scheme].color : '');
}, function (props) {
  return get$1(schemeMap[props.scheme] ? schemeMap[props.scheme].bg : '');
}, get$1('colors.blackfade15'), function (props) {
  return props.full ? '1px 0px' : '1px';
}, function (props) {
  return props.full ? '0' : '3px';
}, function (props) {
  return props.full ? '-1px' : '0';
}, COMMON);
Flash.defaultProps = {
  theme: theme,
  scheme: 'base'
};
Flash.propTypes = _objectSpread({
  children: PropTypes.node,
  full: PropTypes.bool,
  scheme: PropTypes.oneOf(Object.keys(schemeMap)),
  theme: PropTypes.object
}, COMMON.propTypes);

var Heading = styled.h1.withConfig({
  displayName: "Heading",
  componentId: "sc-1m35ws5-0"
})(["font-weight:", ";font-size:", ";", " ", ";"], get$1('fontWeights.bold'), get$1('fontSizes.5'), TYPOGRAPHY, COMMON);
Heading.defaultProps = {
  theme: theme,
  m: 0
};
Heading.propTypes = _objectSpread({}, COMMON.propTypes, TYPOGRAPHY.propTypes, {
  theme: PropTypes.object
});

var sizeMap = {
  small: "padding 0.125em ".concat(theme.space[1], "px; font-size: ").concat(theme.fontSizes[0], "px;"),
  medium: "padding: 3px ".concat(theme.space[1], "px; font-size: ").concat(theme.fontSizes[0], "px;"),
  large: "padding: 4px ".concat(theme.space[2], "px; ").concat(theme.fontSizes[1], "px;"),
  xl: "padding: ".concat(theme.space[1], "px; ").concat(theme.space[2], "px; ").concat(theme.fontSizes[2], "px;")
};
var outlineStyles = "\n  margin-top: -1px; // offsets the 1px border\n  margin-bottom: -1px; // offsets the 1px border\n  font-weight: 400;\n  color: ".concat(colors.gray[6], ";\n  background-color: transparent;\n  border: ").concat(theme.borders[1], " ").concat(colors.blackfade15, ";\n  box-shadow: none;\n");
var Label = styled('span').withConfig({
  displayName: "Label",
  componentId: "sc-1lj9poe-0"
})(["display:inline-block;font-weight:600;line-height:", ";color:", ";border-radius:2px;&:hover{text-decoration:none;}", " ", ";", ";", ";"], theme.lineHeights.condensedUltra, colors.white, COMMON, function (props) {
  return props.dropshadow ? 'box-shadow: inset 0 -1px 0 rgba(27, 31, 35, 0.12)' : '';
}, function (props) {
  return sizeMap[props.size];
}, function (props) {
  return props.outline ? outlineStyles : '';
});
Label.defaultProps = {
  theme: theme,
  bg: 'gray.5',
  size: 'medium'
};
Label.propTypes = _objectSpread({
  dropshadow: PropTypes.bool,
  outline: PropTypes.bool,
  theme: PropTypes.object
}, COMMON.propTypes);

var buttonStyles = {
  display: 'inline-block',
  padding: '0',
  fontSize: 'inherit',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  backgroundColor: 'transparent',
  border: '0',
  appearance: 'none'
};
var hoverColor = style({
  prop: 'hoverColor',
  cssProperty: 'color',
  key: 'colors'
});
var Link = styled.a.withConfig({
  displayName: "Link",
  componentId: "kudw2l-0"
})(["text-decoration:", ";&:hover{text-decoration:underline;", ";}", ";", " ", ";"], function (props) {
  return props.underline ? 'underline' : 'none';
}, hoverColor, function (props) {
  return props.is === 'button' ? buttonStyles : '';
}, TYPOGRAPHY, COMMON);
Link.defaultProps = {
  theme: theme,
  color: 'blue.5'
};
Link.propTypes = _objectSpread({
  as: PropTypes.oneOf(['a', 'button', 'input', 'summary']),
  href: PropTypes.string,
  theme: PropTypes.object,
  underline: PropTypes.bool
}, TYPOGRAPHY.propTypes, COMMON.propTypes);

function PointerBox(props) {
  // don't destructure these, just grab them
  var bg = props.bg,
      border = props.border,
      borderColor = props.borderColor,
      as = props.as;

  var caret = props.caret,
      children = props.children,
      boxProps = _objectWithoutProperties(props, ["caret", "children"]);

  var caretProps = {
    bg: bg,
    borderColor: borderColor,
    borderWidth: border,
    location: caret
  };
  return React.createElement(Relative, {
    as: as
  }, React.createElement(BorderBox, boxProps, children, React.createElement(Caret, caretProps)));
}

PointerBox.displayName = "PointerBox";
PointerBox.defaultProps = {
  theme: theme
};

var StyledOcticon = styled(Octicon)(COMMON);
StyledOcticon.defaultProps = {
  theme: theme
};
StyledOcticon.propTypes = _objectSpread({}, COMMON.propTypes, {
  theme: PropTypes.object
});

var statusMap = {
  issueClosed: colors.red[6],
  pullClosed: colors.red[6],
  pullMerged: colors.purple[5],
  issueOpened: '#2cbe4e',
  // This was generated by a sass function in Primer, so using a hex here
  pullOpened: '#2cbe4e',
  // This was generated by a sass function in Primer, so using a hex here
  gray: colors.gray[5]
};
var octiconMap = {
  issueOpened: IssueOpened,
  pullOpened: GitPullRequest,
  issueClosed: IssueClosed,
  pullClosed: GitPullRequest,
  pullMerged: GitMerge
};

function StateLabelBase(_ref) {
  var className = _ref.className,
      status = _ref.status,
      _ref$small = _ref.small,
      small = _ref$small === void 0 ? false : _ref$small,
      children = _ref.children;
  var octiconProps = small ? {
    width: '1em'
  } : {};
  return React.createElement("span", {
    className: className
  }, status && React.createElement(StyledOcticon, _extends({
    mr: 1
  }, octiconProps, {
    icon: octiconMap[status]
  })), children);
}

StateLabelBase.displayName = "StateLabelBase";
var StateLabel = styled(StateLabelBase).withConfig({
  displayName: "StateLabel",
  componentId: "bvwoe0-0"
})(["display:inline-flex;align-items:center;padding:", ";font-weight:600;line-height:20px;color:", ";font-size:", ";text-align:center;background-color:", ";border-radius:", "px;", ";"], function (props) {
  return props.small ? "0.125em ".concat(theme.space[1], "px") : "".concat(theme.space[1], "px ").concat(theme.space[2], "px");
}, colors.white, function (props) {
  return props.small ? "".concat(theme.fontSizes[0], "px") : "".concat(theme.fontSizes[1], "px");
}, function (props) {
  return props.status ? statusMap[props.status] : statusMap.gray;
}, theme.radii[1], COMMON);
StateLabel.defaultProps = {
  theme: theme
};
StateLabel.propTypes = _objectSpread({
  small: PropTypes.bool,
  status: PropTypes.oneOf(['issueOpened', 'pullOpened', 'issueClosed', 'pullClosed', 'pullMerged']),
  theme: PropTypes.object
}, COMMON.propTypes);

function TextInputBase(_ref) {
  var autocomplete = _ref.autocomplete,
      theme$$1 = _ref.theme,
      size = _ref.size,
      block = _ref.block,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["autocomplete", "theme", "size", "block", "className"]);

  var classes = classnames(className, 'form-control', {
    'input-block': block,
    'input-sm': size === 'small',
    'input-lg': size === 'large'
  });

  var inputProps = _objectSpread({
    className: classes,
    autoComplete: autocomplete,
    type: 'text'
  }, rest);

  return React.createElement("input", inputProps);
}

TextInputBase.displayName = "TextInputBase";
var TextInput = styled(TextInputBase).withConfig({
  displayName: "TextInput",
  componentId: "sc-156lfck-0"
})(["min-height:34px;padding:6px ", "px;font-size:", "px;line-height:20px;color:", ";vertical-align:middle;background-color:", ";background-repeat:no-repeat;background-position:right 8px center;border:1px solid ", ";border-radius:", "px;outline:none;box-shadow:", ";&:focus{border-color:", ";outline:none;box-shadow:", ",", ";}&.input-sm{min-height:28px;padding-top:3px;padding-bottom:3px;font-size:$font-size-small;line-height:20px;}&.input-lg{padding:$spacer-1 10px;font-size:$h4-size;}&.input-block{display:block;width:100%;}@media (max-width:", "){font-size:", "px;}", ";"], get$1('space.2'), get$1('fontSizes.2'), get$1('colors.gray.9'), get$1('colors.white'), get$1('colors.gray.3'), get$1('radii.1'), get$1('shadows.formControl'), get$1('colors.blue.4'), get$1('shadows.formControl'), get$1('shadows.formControlFocus'), get$1('breakpoints.1'), get$1('fontSizes.1'), COMMON);
TextInput.defaultProps = {
  theme: theme
};
TextInput.propTypes = _objectSpread({
  autocomplete: PropTypes.string,
  block: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'large']),
  theme: PropTypes.object,
  value: PropTypes.string
}, COMMON.propTypes);

var Text = styled.span.withConfig({
  displayName: "Text",
  componentId: "sc-1g6etse-0"
})(["", " ", ";"], TYPOGRAPHY, COMMON);
Text.defaultProps = {
  theme: theme
};
Text.propTypes = _objectSpread({}, TYPOGRAPHY.propTypes, COMMON.propTypes, {
  theme: PropTypes.object
});

function TooltipBase(_ref) {
  var direction = _ref.direction,
      children = _ref.children,
      className = _ref.className,
      text = _ref.text,
      noDelay = _ref.noDelay,
      align = _ref.align,
      wrap = _ref.wrap;
  var classes = classnames(className, "tooltipped-".concat(direction), align && "tooltipped-align-".concat(align, "-2"), noDelay && 'tooltipped-no-delay', wrap && 'tooltipped-multiline');
  return React.createElement("span", {
    "aria-label": text,
    className: classes
  }, children);
}

TooltipBase.displayName = "TooltipBase";
var Tooltip = styled(TooltipBase).withConfig({
  displayName: "Tooltip",
  componentId: "h3c7f-0"
})(["position:relative;&::before{position:absolute;z-index:1000001;display:none;width:0px;height:0px;color:", ";pointer-events:none;content:'';border:6px solid transparent;opacity:0;}&::after{position:absolute;z-index:1000000;display:none;padding:0.5em 0.75em;font:normal normal 11px/1.5 ", ";-webkit-font-smoothing:subpixel-antialiased;color:", ";text-align:center;text-decoration:none;text-shadow:none;text-transform:none;letter-spacing:normal;word-wrap:break-word;white-space:pre;pointer-events:none;content:attr(aria-label);background:", ";border-radius:", "px;opacity:0;}@keyframes tooltip-appear{from{opacity:0;}to{opacity:1;}}&:hover,&:active,&:focus{&::before,&::after{display:inline-block;text-decoration:none;animation-name:tooltip-appear;animation-duration:0.1s;animation-fill-mode:forwards;animation-timing-function:ease-in;animation-delay:0.4s;}}&.tooltipped-no-delay:hover,&.tooltipped-no-delay:active,&.tooltipped-no-delay:focus{&::before,&::after{animation-delay:0s;}}&.tooltipped-multiline:hover,&.tooltipped-multiline:active,&.tooltipped-multiline:focus{&::after{display:table-cell;}}&.tooltipped-s,&.tooltipped-se,&.tooltipped-sw{&::after{top:100%;right:50%;margin-top:6px;}&::before{top:auto;right:50%;bottom:-7px;margin-right:-6px;border-bottom-color:", ";}}&.tooltipped-se{&::after{right:auto;left:50%;margin-left:-", "px;}}&.tooltipped-sw::after{margin-right:-", "px;}&.tooltipped-n,&.tooltipped-ne,&.tooltipped-nw{&::after{right:50%;bottom:100%;margin-bottom:6px;}&::before{top:-7px;right:50%;bottom:auto;margin-right:-6px;border-top-color:", ";}}&.tooltipped-ne{&::after{right:auto;left:50%;margin-left:-", "px;}}&.tooltipped-nw::after{margin-right:-", "px;}&.tooltipped-s::after,&.tooltipped-n::after{transform:translateX(50%);}&.tooltipped-w{&::after{right:100%;bottom:50%;margin-right:6px;transform:translateY(50%);}&::before{top:50%;bottom:50%;left:-7px;margin-top:-6px;border-left-color:", ";}}&.tooltipped-e{&::after{bottom:50%;left:100%;margin-left:6px;transform:translateY(50%);}&::before{top:50%;right:-7px;bottom:50%;margin-top:-6px;border-right-color:", ";}}&.tooltipped-multiline{&::after{width:max-content;max-width:250px;word-wrap:break-word;white-space:pre-line;border-collapse:separate;}&.tooltipped-s::after,&.tooltipped-n::after{right:auto;left:50%;transform:translateX(-50%);}&.tooltipped-w::after,&.tooltipped-e::after{right:100%;}}&.tooltipped-align-right-2::after{right:0;margin-right:0;}&.tooltipped-align-right-2::before{right:15px;}&.tooltipped-align-left-2::after{left:0;margin-left:0;}&.tooltipped-align-left-2::before{left:10px;}", ";"], get$1('colors.black'), get$1('fonts.normal'), get$1('colors.white'), get$1('colors.black'), get$1('radii.1'), get$1('colors.black'), get$1('space.3'), get$1('space.3'), get$1('colors.black'), get$1('space.3'), get$1('space.3'), get$1('colors.black'), get$1('colors.black'), COMMON);
Tooltip.alignments = ['left', 'right'];
Tooltip.directions = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
Tooltip.defaultProps = {
  theme: theme,
  direction: 'n'
};
Tooltip.propTypes = _objectSpread({
  align: PropTypes.oneOf(Tooltip.alignments),
  children: PropTypes.node,
  direction: PropTypes.oneOf(Tooltip.directions),
  noDelay: PropTypes.bool,
  text: PropTypes.string,
  theme: PropTypes.object,
  wrap: PropTypes.bool
}, COMMON.propTypes);

var ITEM_CLASS = 'UnderlineNav-item';
var SELECTED_CLASS = 'selected';

function UnderlineNavBase(_ref) {
  var actions = _ref.actions,
      className = _ref.className,
      align = _ref.align,
      children = _ref.children,
      full = _ref.full,
      label = _ref.label;
  var classes = classnames(className, 'UnderlineNav', align && "UnderlineNav--".concat(align), full && 'UnderlineNav--full');
  return React.createElement("nav", {
    className: classes,
    "aria-label": label
  }, React.createElement("div", {
    className: "UnderlineNav-body"
  }, children), actions && React.createElement("div", {
    className: "UnderlineNav-actions"
  }, actions));
}

UnderlineNavBase.displayName = "UnderlineNavBase";
var UnderlineNav = styled(UnderlineNavBase).withConfig({
  displayName: "UnderlineNav",
  componentId: "sc-14g1rj0-0"
})(["display:flex;justify-content:space-between;border-bottom:1px solid ", ";&.UnderlineNav--right{justify-content:flex-end;.UnderlineNav-item{margin-right:0;margin-left:", "px;}.UnderlineNav-actions{flex:1 1 auto;}}&.UnderlineNav--full{display:block;}.UnderlineNav-body{display:flex;margin-bottom:-1px;}.UnderlineNav-actions{align-self:center;}", ";"], get$1('colors.gray.2'), get$1('space.3'), COMMON);
UnderlineNav.Link = styled.a.attrs(function (props) {
  return {
    activeClassName: typeof props.to === 'string' ? 'selected' : '',
    className: classnames(ITEM_CLASS, props.selected && SELECTED_CLASS, props.className)
  };
}).withConfig({
  displayName: "UnderlineNav__Link",
  componentId: "sc-14g1rj0-1"
})(["padding:", "px ", "px;margin-right:", "px;font-size:", "px;line-height:", ";color:", ";text-align:center;border-bottom:2px solid transparent;text-decoration:none;&:hover,&:focus{color:", ";text-decoration:none;border-bottom-color:", ";transition:0.2s ease;.UnderlineNav-octicon{color:", ";}}&.selected{font-weight:", ";color:", ";border-bottom-color:", ";.UnderlineNav-octicon{color:", ";}}"], get$1('space.3'), get$1('space.2'), get$1('space.3'), get$1('fontSizes.1'), get$1('lineHeights.default'), get$1('colors.gray.6'), get$1('colors.gray.9'), get$1('colors.gray.3'), get$1('colors.gray.5'), get$1('fontWeights.bold'), get$1('colors.gray.9'), get$1('colors.orange.6'), get$1('colors.gray.5'));
UnderlineNav.defaultProps = {
  theme: theme
};
UnderlineNav.propTypes = _objectSpread({
  actions: PropTypes.node,
  align: PropTypes.oneOf(['right']),
  children: PropTypes.node,
  full: PropTypes.bool,
  label: PropTypes.string,
  theme: PropTypes.object
}, COMMON.propTypes);
UnderlineNav.Link.defaultProps = {
  theme: theme
};
UnderlineNav.Link.propTypes = _objectSpread({
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  selected: PropTypes.bool
}, COMMON.propTypes);

export { theme, BaseStyles, BorderBox, Box, Flex, Position, Absolute, Fixed, Relative, Sticky, Avatar, AvatarPair, BranchName, ButtonDanger, ButtonOutline, ButtonPrimary, Button, Caret, CircleBadge, CircleOcticon, CounterLabel, Details, Dropdown, FilterList, Flash, Heading, Label, Link, PointerBox, StateLabel, StyledOcticon, TextInput, Text, Tooltip, UnderlineNav };
